import React, { useState } from 'react';
import { useLocalContext } from '../../context/LocalContext';
import './Auth.css';

const SignupModal = () => {
  const { 
    isSignupModalOpen, 
    closeSignupModal, 
    openLoginModal, 
    signup, 
    isLoading, 
    formData, 
    updateFormData,
    showNotification 
  } = useLocalContext();

  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.name) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.birthday) {
      newErrors.birthday = 'Birthday is required';
    } else {
      const age = new Date().getFullYear() - new Date(formData.birthday).getFullYear();
      if (age < 18) {
        newErrors.birthday = 'You must be at least 18 years old';
      }
    }
    
    if (!formData.gender) {
      newErrors.gender = 'Please select your gender';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handlePrevStep = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) return;
    
    const result = await signup({
      email: formData.email,
      password: formData.password,
      name: formData.name,
      birthday: formData.birthday,
      gender: formData.gender,
      phone: formData.phone
    });
    
    if (!result.success) {
      showNotification(result.error || 'Signup failed', 'error');
    }
  };

  const handleClose = () => {
    closeSignupModal();
    setErrors({});
    setStep(1);
  };

  const switchToLogin = () => {
    closeSignupModal();
    openLoginModal();
  };

  if (!isSignupModalOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal signup-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="modal-header">
          <div className="modal-logo">
            <svg viewBox="0 0 100 30" className="tinder-logo-modal">
              <g fill="currentColor">
                <path d="M10.5 5.5c-.2-.4-.6-.7-1.1-.7H7.2c-.5 0-.9.3-1.1.7L.1 18.3c-.2.4-.1.8.1 1.1.2.3.6.5 1 .5h2.4c.5 0 .9-.3 1.1-.7l5.8-13.6c.2-.4.1-.8 0-1.1z"/>
                <path d="M23.5 5.5c-.2-.4-.6-.7-1.1-.7h-2.2c-.5 0-.9.3-1.1.7l-6 13.9c-.2.4-.1.8.1 1.1.2.3.6.5 1 .5h2.4c.5 0 .9-.3 1.1-.7l5.8-13.6c.2-.4.1-.8 0-1.2z"/>
                <path d="M36.2 5.5c-.2-.4-.6-.7-1.1-.7h-2.2c-.5 0-.9.3-1.1.7l-6 13.9c-.2.4-.1.8.1 1.1.2.3.6.5 1 .5h2.4c.5 0 .9-.3 1.1-.7l5.8-13.6c.2-.4.1-.8 0-1.2z"/>
                <path d="M48.7 8.4c.4 0 .8-.2 1-.5l1.4-2.1c.2-.3.2-.7.1-1-.2-.3-.5-.5-.9-.5H38c-.4 0-.7.2-.9.5-.2.3-.2.7-.1 1l1.4 2.1c.2.3.6.5 1 .5h2.8v11.5c0 .6.4 1 1 1h2.5c.6 0 1-.4 1-1V8.4h2zm12.6-3.6h-2.5c-.6 0-1 .4-1 1v13.1c0 .6.4 1 1 1h2.5c.6 0 1-.4 1-1V5.8c0-.6-.4-1-1-1zm15.8 0h-2.5c-.6 0-1 .4-1 1v7.7l-6.1-8.2c-.2-.3-.5-.5-.9-.5h-2.1c-.6 0-1 .4-1 1v13.1c0 .6.4 1 1 1h2.5c.6 0 1-.4 1-1v-7.7l6.1 8.2c.2.3.5.5.9.5h2.1c.6 0 1-.4 1-1V5.8c0-.6-.4-1-1-1zm14.5 0H82c-.4 0-.7.2-.9.5-.2.3-.2.7-.1 1l1.4 2.1c.2.3.6.5 1 .5h2.8v11.5c0 .6.4 1 1 1h2.5c.6 0 1-.4 1-1V8.4h2.8c.4 0 .8-.2 1-.5l1.4-2.1c.2-.3.2-.7.1-1-.2-.3-.5-.5-.9-.5z"/>
              </g>
            </svg>
          </div>
          <h2 className="modal-title">Create account</h2>
          <p className="modal-subtitle">
            {step === 1 ? 'Start your journey to find love' : 'Tell us about yourself'}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="progress-steps">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1</div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2</div>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          {step === 1 ? (
            <>
              <div className="form-group">
                <label htmlFor="signup-email">Email</label>
                <input
                  type="email"
                  id="signup-email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="signup-password">Password</label>
                <div className="password-input">
                  <input
                    type={formData.showPassword ? 'text' : 'password'}
                    id="signup-password"
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={(e) => updateFormData('password', e.target.value)}
                    className={errors.password ? 'error' : ''}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => updateFormData('showPassword', !formData.showPassword)}
                  >
                    {formData.showPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <span className="error-text">{errors.password}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="signup-confirm-password">Confirm Password</label>
                <input
                  type="password"
                  id="signup-confirm-password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => updateFormData('confirmPassword', e.target.value)}
                  className={errors.confirmPassword ? 'error' : ''}
                />
                {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
              </div>

              <button type="button" className="btn btn-primary btn-block btn-lg" onClick={handleNextStep}>
                Continue
              </button>
            </>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="signup-name">Full Name</label>
                <input
                  type="text"
                  id="signup-name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  className={errors.name ? 'error' : ''}
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="signup-birthday">Birthday</label>
                <input
                  type="date"
                  id="signup-birthday"
                  value={formData.birthday}
                  onChange={(e) => updateFormData('birthday', e.target.value)}
                  className={errors.birthday ? 'error' : ''}
                />
                {errors.birthday && <span className="error-text">{errors.birthday}</span>}
              </div>

              <div className="form-group">
                <label>Gender</label>
                <div className="gender-options">
                  <label className={`gender-option ${formData.gender === 'male' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={formData.gender === 'male'}
                      onChange={(e) => updateFormData('gender', e.target.value)}
                    />
                    <span>Male</span>
                  </label>
                  <label className={`gender-option ${formData.gender === 'female' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={formData.gender === 'female'}
                      onChange={(e) => updateFormData('gender', e.target.value)}
                    />
                    <span>Female</span>
                  </label>
                  <label className={`gender-option ${formData.gender === 'other' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="gender"
                      value="other"
                      checked={formData.gender === 'other'}
                      onChange={(e) => updateFormData('gender', e.target.value)}
                    />
                    <span>Other</span>
                  </label>
                </div>
                {errors.gender && <span className="error-text">{errors.gender}</span>}
              </div>

              <div className="form-buttons">
                <button type="button" className="btn btn-outline btn-lg" onClick={handlePrevStep}>
                  Back
                </button>
                <button type="submit" className="btn btn-primary btn-lg" disabled={isLoading}>
                  {isLoading ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </div>
            </>
          )}
        </form>

        <div className="modal-divider">
          <span>or</span>
        </div>

        <div className="social-login">
          <button className="social-btn google">
            <svg viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </div>

        <div className="modal-footer">
          <p>
            Already have an account?{' '}
            <button className="link-btn" onClick={switchToLogin}>
              Log in
            </button>
          </p>
        </div>

        <p className="modal-terms">
          By signing up, you agree to our{' '}
          <a href="/terms">Terms of Service</a> and{' '}
          <a href="/privacy">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default SignupModal;
